import { useEffect, useRef, useState } from "react";

/**
 * True once the page is scrolled past the top — drives the header's scroll-on
 * treatment (`.appbar.is-scrolled`). SSR-safe: starts false (flat header), the
 * listener attaches after hydration, so server and first client render match.
 * Threshold 12px per the Main Style scroll-overlap spec (T226).
 */
export function useHeaderScrolled(threshold = 12): boolean {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll(); // sync once on mount (e.g. restored scroll position)
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);

  return scrolled;
}

/** T872 S1 — the public header compacts past this scroll offset (design pass, 2026-10-08). */
export const HEADER_COMPACT_THRESHOLD = 32;
/** T872 S1 — on a phone the bar may hide only once the page is this far down. */
export const HEADER_HIDE_AFTER = 80;
/** T872 S1 — accumulated same-direction scroll needed before the bar hides or shows again. */
export const HEADER_DELTA_TOLERANCE = 8;
/** The shell's single breakpoint (T107): below it the cúter owns navigation. */
const HEADER_MOBILE_QUERY = "(max-width: 47.99875em)";

export interface HeaderScrollState {
  /** Past 12px — R-T226-3, the same threshold `useHeaderScrolled` keeps. */
  scrolled: boolean;
  /** Past 32px — desktop bar takes its compact height (R-T872-2). */
  compact: boolean;
  /** Phone only: scrolled down past 80px with ≥8px of accumulated downward travel; any
   *  ≥8px of upward travel shows it again. Never true while `pinned`, on desktop, or
   *  within the first 32px (R-T872-2). Keyboard focus and open panels are handled in
   *  CSS (`:focus-within`, `:has([aria-expanded="true"])`), so they reveal the bar
   *  without waiting for a scroll event. */
  hidden: boolean;
}

interface HeaderScrollOptions {
  /** True while the cúter or another panel owned by the header is open: the bar stays. */
  pinned?: boolean;
}

/**
 * T872 S1 — one scroll engine for the public header (R-T872-2). Builds on
 * `useHeaderScrolled` (R-T226-3 untouched) and adds the two states the October
 * delivery decided: `compact` on desktop, `hidden` on a phone while scrolling down.
 * SSR-safe like its base: every flag starts false and only moves after hydration.
 */
export function useHeaderScrollState({ pinned = false }: HeaderScrollOptions = {}): HeaderScrollState {
  const scrolled = useHeaderScrolled();
  const [compact, setCompact] = useState(false);
  const [hidden, setHidden] = useState(false);
  const lastY = useRef(0);
  const travel = useRef(0);

  useEffect(() => {
    const mq =
      typeof window.matchMedia === "function" ? window.matchMedia(HEADER_MOBILE_QUERY) : null;
    const isMobile = () => mq?.matches ?? false;

    const read = () => {
      const y = window.scrollY;
      const dy = y - lastY.current;
      // Accumulate travel in one direction; a direction change starts the count over,
      // so a 2px wobble while reading never flips the bar.
      if ((dy > 0 && travel.current < 0) || (dy < 0 && travel.current > 0)) travel.current = 0;
      travel.current += dy;
      lastY.current = y;

      setCompact(y > HEADER_COMPACT_THRESHOLD);

      if (!isMobile() || pinned || y <= HEADER_COMPACT_THRESHOLD) {
        setHidden(false);
      } else if (travel.current > HEADER_DELTA_TOLERANCE && y > HEADER_HIDE_AFTER) {
        setHidden(true);
      } else if (travel.current < -HEADER_DELTA_TOLERANCE) {
        setHidden(false);
      }
    };

    // Orientation change or a resize across the breakpoint: forget the travel and show
    // the bar — the decision says the bar must be visible after a rotation.
    const reset = () => {
      travel.current = 0;
      lastY.current = window.scrollY;
      setHidden(false);
      setCompact(window.scrollY > HEADER_COMPACT_THRESHOLD);
    };

    lastY.current = window.scrollY;
    read();
    window.addEventListener("scroll", read, { passive: true });
    window.addEventListener("resize", reset);
    mq?.addEventListener?.("change", reset);
    return () => {
      window.removeEventListener("scroll", read);
      window.removeEventListener("resize", reset);
      mq?.removeEventListener?.("change", reset);
    };
  }, [pinned]);

  return { scrolled, compact, hidden };
}
